import {
  ClearButton,
  DataSubjectInputType,
  DATE_FORMAT,
  DatePicker,
  formatCodeName,
  formatDateTimeShort,
  FormLabel,
  getDayjs,
  InputDataSubjectDetailsUrl,
  ISO_DATE_FORMAT,
  ISODate,
  Table,
  TextEllipsis,
  TextLink,
  useSystemClassifierItemsBy,
  useSystemClassifierSelectOptions,
  useTableStoreValue,
} from '@atlas-frontend/sdk';
import translations from './DataSubjectInputsTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { SampleSubjectLockIcon } from '../sample/sampleSubjectLock/SampleSubjectLockIcon';
import { expand } from 'uri-template-lite';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-inputs-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<DataSubjectInputType>();

export const DataSubjectInputsTable: FC<{
  data: DataSubjectInputType[];
  isLoading: boolean;
  dataSubjectCode: string;
  valueDate: ISODate | null;
  onChangeValueDate: (date: ISODate | null) => void;
}> = ({ data, isLoading, dataSubjectCode, valueDate, onChangeValueDate }) => {
  const { t } = useTranslation();
  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'FREQUENCY',
    'DATA_LOADING_STATUS',
  ]);
  const { FREQUENCY: frequencyOptions, DATA_LOADING_STATUS: statusOptions } =
    useSystemClassifierSelectOptions(
      ['FREQUENCY', 'DATA_LOADING_STATUS'],
      'id',
    );

  const columns = useMemo(
    () => [
      columnHelper.accessor('sourceNames', {
        header: t('columns.sources'),
        meta: { width: '20ch' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor(
        (rowData) =>
          !rowData.systemicGroup &&
          formatCodeName({
            code: rowData.groupCode,
            name: rowData.groupName,
          }),
        {
          id: 'group',
          header: t('columns.group'),
          filterFn: 'includesString',
        },
      ),
      columnHelper.accessor((rowData) => formatCodeName(rowData), {
        id: 'codeName',
        header: t('columns.codeName'),
        filterFn: 'includesString',
        cell: (props) => {
          const { id, groupId, systemicGroup } = props.row.original;
          return (
            <TextEllipsis>
              <TextLink
                id={`open-dataSubject-input-${props.row.index}`}
                href={expand(InputDataSubjectDetailsUrl, {
                  descriptionId: id,
                  groupId: systemicGroup ? undefined : groupId,
                  date: valueDate,
                  subject: dataSubjectCode,
                })}
              >
                {formatCodeName(props.row.original)}
              </TextLink>
            </TextEllipsis>
          );
        },
      }),
      columnHelper.accessor(
        ({ frequencyId }) =>
          systemClassifierItemsById['FREQUENCY'].get(frequencyId)?.name,
        {
          id: 'frequency',
          header: t('columns.frequency'),
          filterFn: 'includesOption',
          meta: { width: '15ch' },
        },
      ),
      columnHelper.accessor('statusId', {
        header: t('columns.status'),
        cell: (props) => {
          const statusId = props.row.original.statusId;
          if (!statusId) return null;
          return (
            <TextEllipsis>
              {
                systemClassifierItemsById['DATA_LOADING_STATUS'].get(statusId)
                  ?.name
              }
            </TextEllipsis>
          );
        },
        filterFn: 'includesOption',
        meta: { width: '25ch' },
      }),
      columnHelper.accessor('dataManager.username', {
        header: t('columns.dataManager'),
        filterFn: 'includesString',
      }),
      columnHelper.accessor('dataModifiedBy.username', {
        header: t('columns.dataModifiedBy'),
        filterFn: 'includesString',
      }),
      columnHelper.accessor(
        ({ dataModifiedAt }) => formatDateTimeShort(dataModifiedAt),
        {
          id: 'dataModifiedAt',
          header: t('columns.dataModifiedAt'),
          meta: { width: '30ch' },
          sortingFn: 'formattedDateTime',
          enableColumnFilter: false,
        },
      ),
      columnHelper.accessor('comment', {
        header: t('columns.comment'),
        enableColumnFilter: false,
      }),
      columnHelper.accessor('locked', {
        header: t('columns.locked'),
        cell: (props) => {
          const { locked, hasActive } = props.row.original;
          if (locked === null) return null;
          return (
            <SampleSubjectLockIcon
              isLocked={locked}
              showBullet={locked && hasActive}
            />
          );
        },
        filterFn: 'includesOption',
        meta: { width: '12ch', textAlign: 'center' },
      }),
    ],
    [dataSubjectCode, systemClassifierItemsById, t, valueDate],
  );

  const options = useMemo(
    () => ({
      frequency: frequencyOptions.map((frequency) => ({
        id: frequency.label,
        name: frequency.label,
      })),
      statusId: statusOptions.map((status) => ({
        id: status.value,
        name: status.label,
      })),
      locked: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
        { id: null, name: t('empty') },
      ],
    }),
    [frequencyOptions, statusOptions, t],
  );

  return (
    <Flex align="end" gap="middle" vertical>
      <Table
        hideClearFilters
        actions={
          <TableActions
            valueDate={valueDate}
            onChangeValueDate={onChangeValueDate}
          />
        }
        columns={columns}
        options={options}
        data={data || []}
        isLoading={isLoading}
        name="subject-inputs-table"
        scrollContainerSelector="#detail-layout-main-content"
        sticky
      />
    </Flex>
  );
};

const TableActions: FC<{
  valueDate: ISODate | null;
  onChangeValueDate: (date: ISODate | null) => void;
}> = ({ valueDate, onChangeValueDate }) => {
  const { t } = useTranslation();
  const [table] = useTableStoreValue('table');

  const clearAllFilters = useCallback(() => {
    table.getAllColumns().forEach((column) => column.setFilterValue(undefined));
    onChangeValueDate(null);
  }, [onChangeValueDate, table]);

  const filterCount =
    table
      .getAllColumns()
      .reduce(
        (count, column) => (column.getIsFiltered() ? count + 1 : count),
        0,
      ) + (valueDate ? 1 : 0);

  return (
    <Flex
      gap="small"
      align="end"
      justify="space-between"
      style={{ width: '100%' }}
    >
      <Flex vertical gap="small">
        <FormLabel htmlFor="valueDate" required>
          {t('valueDate')}
        </FormLabel>
        <DatePicker
          id="valueDate"
          format={DATE_FORMAT}
          value={valueDate ? getDayjs(valueDate, ISO_DATE_FORMAT) : null}
          onChange={(date) =>
            onChangeValueDate(date.format(ISO_DATE_FORMAT) as ISODate)
          }
          allowClear={false}
          style={{ width: '10rem' }}
        />
      </Flex>
      {Boolean(filterCount) && (
        <ClearButton
          id="clear-subject-inputs-table-filters"
          count={filterCount}
          onClick={clearAllFilters}
        />
      )}
    </Flex>
  );
};
